import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Plus } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetExternalApiKeyQueryKey,
  useDeleteExternalApiKey,
  useGetExternalApiKey,
} from "~/api/organizations/organizations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useSettingsParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
// import { shortRelativeTime } from "~/utils/date";
import { trackEvent } from "~/utils/analytics";
import { CreateApiKeyDialog } from "./CreateApiKeyDialog";

export const SettingsDevelopers: FC = () => {
  useTrackPageVisit("app/settings_developers/visit");

  const { organizationId } = useSettingsParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: apiKeysResponse,
    isLoading,
    isError,
  } = useGetExternalApiKey(organizationId);

  const { mutateAsync: deleteApiKey, isPending: isDeleting } =
    useDeleteExternalApiKey();

  const apiKeys = apiKeysResponse?.apiKeys || [];
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);

  const handleCreateApiKey = () => {
    trackEvent({
      eventName: "app/settings_developers/create_api_key.button/click",
    });
    setIsCreateDialogOpen(true);
  };

  const handleDeleteClick = (keyId: string) => {
    trackEvent({
      eventName: "app/settings_developers/delete_key.button/click",
    });
    setSelectedKeyId(keyId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedKeyId) return;

    try {
      await deleteApiKey({
        organizationId,
        id: selectedKeyId,
      });

      queryClient.invalidateQueries({
        queryKey: getGetExternalApiKeyQueryKey(organizationId),
      });

      trackEvent({
        eventName: "app/settings_developers/delete_key.success",
      });

      toast({
        title: intl.formatMessage({
          id: "settings.developers.keyDeleted",
          defaultMessage: "API key deleted successfully",
        }),
      });

      setDeleteDialogOpen(false);
      setSelectedKeyId(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "settings.developers.deleteKeyError",
          defaultMessage: "Failed to delete API key",
        }),
      });
    }
  };

  // const formatDate = (dateString?: string) => {
  //   if (!dateString) return "";
  //   return shortRelativeTime(dateString);
  // };

  const maskApiKey = (key: string): string => {
    if (!key || key.length <= 1) return key;
    // Show first 8 characters and mask the rest with a fixed number of asterisks
    // Show first 8 characters and mask the rest with a fixed number of asterisks
    const visiblePart = key.substring(0, 8);
    const maskedPart = "*".repeat(50);
    return `${visiblePart}${maskedPart}`;
  };

  return (
    <>
      <div className="flex flex-col gap-6 mt-2 w-full pb-12">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-2xl font-semibold">
              <FormattedMessage
                id="settings.developers.apiKeys"
                defaultMessage="API Key"
              />
            </h3>
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.developers.apiKeys.description"
                defaultMessage="Manage your API key for accessing the platform programmatically"
              />
            </p>
          </div>
          <Button onClick={handleCreateApiKey} className="shrink-0 self-start">
            <Plus className="h-4 w-4 mr-2" />
            <FormattedMessage
              id="settings.developers.createApiKey"
              defaultMessage="API Key"
            />
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">
              <FormattedMessage id="loading" defaultMessage="Loading..." />
            </p>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-destructive">
              <FormattedMessage
                id="settings.developers.loadError"
                defaultMessage="Failed to load API key"
              />
            </p>
          </div>
        )}

        {!isLoading && !isError && apiKeys.length === 0 && (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.developers.noKeys"
                defaultMessage="No API keys found. Create your API key to get started."
              />
            </p>
          </div>
        )}

        {!isLoading && !isError && apiKeys.length > 0 && (
          <div className="flex flex-col gap-4">
            {apiKeys.map((apiKey) => (
              <div
                key={apiKey.id}
                className="border rounded-lg p-4 bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium">
                        {apiKey.name ||
                          apiKeysResponse?.organizationName ||
                          "API Key"}
                      </h4>
                      {apiKey.active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-3 min-w-0">
                      <code className="block truncate min-w-0 max-w-full text-sm bg-muted px-3 py-1 rounded font-mono">
                        {maskApiKey(apiKey.externalApiKeyShort)}
                      </code>
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      {/*{apiKey.createdAt && (*/}
                      {/*  <span>*/}
                      {/*    <FormattedMessage*/}
                      {/*      id="settings.developers.lastUpdated"*/}
                      {/*      defaultMessage="Last updated {time}"*/}
                      {/*      values={{*/}
                      {/*        time: formatDate(apiKey.createdAt),*/}
                      {/*      }}*/}
                      {/*    />*/}
                      {/*  </span>*/}
                      {/*)}*/}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={intl.formatMessage({
                          id: "common.moreActions",
                        })}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        destructive
                        onClick={() => handleDeleteClick(apiKey.id)}
                      >
                        <FormattedMessage
                          id="settings.developers.deleteKey"
                          defaultMessage="Delete"
                        />
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateApiKeyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <FormattedMessage
                id="settings.developers.deleteKeyDialog.title"
                defaultMessage="Delete API Key"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <FormattedMessage
                id="settings.developers.deleteKeyDialog.description"
                defaultMessage="Are you sure you want to delete the API key? This action cannot be undone."
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              <FormattedMessage id="delete" defaultMessage="Delete" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
